import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, FilterIcon, XIcon } from '@hero/web-icons';
import type { RegionCity } from '@hero/api';
import { Button } from '../../../ui/Button';
import { RegionSelector } from '../../Region/RegionSelector';
import { VehicleTypeMultiSelector } from '../../Vehicle/VehicleTypeMultiSelector';
import {
  selectVisibleRows,
  usePricingSheetStore,
} from './store/usePricingSheetStore';
import { isFilterActive, type SheetFilters } from './types';

/**
 * Compact filter strip — origin · destination · vehicles. AND-combined; an
 * empty value means "no constraint". Sits between the scope header (whose
 * price list) and the table (the data) so the mental model reads top-down:
 * scope → view → data.
 *
 * State is subscribed from {@link usePricingSheetStore}; the row counts
 * shown on the right come from the same store via the `selectVisibleRows`
 * selector, so the badge and the table can never drift.
 */
export function PricingSheetFilters() {
  const { t } = useTranslation(['m-pricing']);

  const filters = usePricingSheetStore(s => s.filters);
  const setFilters = usePricingSheetStore(s => s.setFilters);
  const clearFilters = usePricingSheetStore(s => s.clearFilters);
  const rowCount = usePricingSheetStore(s => s.rows.length);
  const visibleCount = usePricingSheetStore(
    s => selectVisibleRows(s).length,
  );

  const active = useMemo(() => isFilterActive(filters), [filters]);

  const setOrigin = (region: RegionCity | null) =>
    setFilters({ ...filters, origin: region });
  const setDestination = (region: RegionCity | null) =>
    setFilters({ ...filters, destination: region });
  const setVehicles = (vehicles: SheetFilters['vehicles']) =>
    setFilters({ ...filters, vehicles });

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-center gap-1.5 pe-2 text-[10px] font-medium uppercase tracking-wider text-gray-400">
        <FilterIcon className="size-3" />
        {t('sheet.filters.label', { defaultValue: 'Filter' })}
      </div>

      <RegionSelector
        size="sm"
        types={['city']}
        value={filters.origin ? String(filters.origin._id) : null}
        onChange={setOrigin}
        placeholder={t('sheet.filters.from_placeholder', { defaultValue: 'From any' })}
        drawerTitle={t('sheet.filters.from_drawer_title', {
          defaultValue: 'Filter by origin city',
        })}
        className="w-[160px]"
      />

      <ArrowRightIcon className="size-3 text-gray-300 rtl:rotate-180" />

      <RegionSelector
        size="sm"
        types={['city']}
        value={filters.destination ? String(filters.destination._id) : null}
        onChange={setDestination}
        placeholder={t('sheet.filters.to_placeholder', { defaultValue: 'To any' })}
        drawerTitle={t('sheet.filters.to_drawer_title', {
          defaultValue: 'Filter by destination city',
        })}
        className="w-[160px]"
      />

      <VehicleTypeMultiSelector
        size="sm"
        value={filters.vehicles}
        onChange={setVehicles}
        placeholder={t('sheet.filters.vehicles_placeholder', {
          defaultValue: 'Any vehicle',
        })}
        className="w-[200px]"
      />

      <div className="ms-auto flex items-center gap-2">
        {active && (
          <span className="text-[11px] font-medium text-gray-500">
            {t('sheet.filters.showing', {
              defaultValue: '{{visible}} of {{total}}',
              visible: visibleCount,
              total: rowCount,
            })}
          </span>
        )}
        {active && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs text-gray-500 hover:text-gray-700"
            onClick={clearFilters}>
            <XIcon className="size-3" />
            {t('sheet.filters.clear', { defaultValue: 'Clear' })}
          </Button>
        )}
      </div>
    </div>
  );
}
